import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { colors, colorConstants, paddingSizes, marginSizes, borderRadiusSizes, iconSizes } from 'theme/default'
import Div from 'components/Div'
import DivButton from 'components/DivButton'
import Flex from 'components/Flex'
import Input from 'components/Input'
import Loading from 'components/Loading'
import IconClose from 'components/Icons/IconClose'
import IconSearch from 'components/Icons/IconSearch'
import IconAddressPin from 'components/Icons/IconAddressPin'
import Transition from 'components/Transition'
import reactStringReplace from 'react-string-replace'

const SearchResultItemWrapper = styled(Flex).attrs(() => ({
  paddingRight: paddingSizes.medium
}))`
  ${props => props.isHighlight ? `background-color: ${colors.background};` : ''}
  &:hover {
    ${props => (props.withoutPointer || props.isHighlight) ? '' : `background-color: ${colorConstants.lightestGray};`}
  }
`

const SearchResultItemContainer = styled(DivButton).attrs(() => ({
  paddingTop: paddingSizes.medium,
  paddingBottom: paddingSizes.medium
}))`
  border-bottom: 1px ${colors.separator} solid;
  flex-grow: 1;
  ${props => props.withoutPointer ? 'cursor: auto;' : ''}
`

const IconPinContainer = styled(Flex).attrs(() => ({
  width: 40,
  justifyContent: 'center',
  alignItems: 'center'
}))`
  min-width: 40px;
`

export const SearchResultItem = React.forwardRef(({ searchString, value, withoutPointer, ...props }, ref) => {
  const highlightedValue = reactStringReplace(value, searchString, (match) => <b>{match}</b>)
  return (
    <SearchResultItemWrapper ref={ref} withoutPointer={withoutPointer} {...props}>
      <IconPinContainer>
        <IconAddressPin fill={colors.text} iconSize={iconSizes.large} />
      </IconPinContainer>
      <SearchResultItemContainer withoutPointer={withoutPointer}>
        {highlightedValue}
      </SearchResultItemContainer>
    </SearchResultItemWrapper>
  )
})
SearchResultItem.propTypes = {
  value: PropTypes.string,
  searchString: PropTypes.string,
  withoutPointer: PropTypes.bool
}

const SearchDropdown = styled(Div).attrs(() => ({
  borderRadius: borderRadiusSizes.small,
  marginBottom: marginSizes.small
}))`
  width: 100%;
  border: 1px solid ${colors.separator};
  background-color: ${colorConstants.white};
  overflow: auto;

  /* stylelint-disable selector-type-no-unknown */
  & ${SearchResultItemWrapper}:last-child ${SearchResultItemContainer} {
    border-bottom: none;
  }

  /* stylelint-enable selector-type-no-unknown */
`

// Fix UI glitch on Chrome
const StyledTransition = styled(Transition)`
  position: absolute;
  top: 100%;
  width: 100%;
  z-index: 2;
`

export const SearchDropdownContainer = ({ show, onClosed, ...props }) => (
  <StyledTransition show={show} onExited={onClosed} mountOnEnter unmountOnExit>
    <SearchDropdown {...props} />
  </StyledTransition>
)
SearchDropdownContainer.propTypes = {
  show: PropTypes.bool,
  onClosed: PropTypes.func
}

const StyledInputAddOn = styled(Input.AddOn)`
  width: 40px;
  padding: 0;
  flex-shrink: 0;
  cursor: pointer;
  &&& {
    background-color: transparent;
  }
`

const SearchStatusContainer = styled(StyledInputAddOn)`
  margin-right: -10px;
`

const ClearButtonContainer = styled(StyledInputAddOn)`
  cursor: pointer;
`

export const InputField = ({ loading, reset, hasReset, ...props }) => (
  <Input {...props} controlled>
    {({ inputElement }) => (
      <React.Fragment>
        <SearchStatusContainer>
          {loading
            ? <Loading color={colors.text} />
            : hasReset
              ? <IconAddressPin fill={colors.text} iconSize={iconSizes.large} />
              : <IconSearch fill={colors.text} />}
        </SearchStatusContainer>
        {inputElement}
        {hasReset && (
          <ClearButtonContainer data-testid='addressSearchInput.resetBtn' onClick={reset}>
            <IconClose fill={colors.text} iconSize={iconSizes.small} />
          </ClearButtonContainer>
        )}
      </React.Fragment>
    )}
  </Input>
)
InputField.propTypes = {
  loading: PropTypes.bool,
  reset: PropTypes.func,
  hasReset: PropTypes.bool
}
